"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import axiosInstance from "../../../utils/axiosInstance"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { CalendarDays, Camera, Clock, Loader2, ChevronLeft, ChevronRight, CheckCircle, XCircle, TrendingUp } from "lucide-react"
import StudentCalendarModal from "../../Common/StudentCalendarView"
import { API_ENDPOINTS } from "../../../constants/config"
import Pagination from "../../Common/Pagination"

const getAuthHeaders = () => {
  const token = localStorage.getItem("token")
  return token ? { Authorization: `Bearer ${token}` } : {}
}

const getCurrentUser = () => {
  const userRaw = localStorage.getItem("user")
  if (!userRaw) {
    throw new Error("User session not found")
  }
  const user = JSON.parse(userRaw)
  const userId = String(user.id || user._id || "")
  if (!userId) {
    throw new Error("Invalid user session")
  }
  return { ...user, userId }
}

const getCurrentLocation = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by your browser"))
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        })
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          reject(new Error("Please allow location access to mark attendance"))
          return
        }
        if (error.code === error.POSITION_UNAVAILABLE) {
          reject(new Error("Location information is unavailable"))
          return
        }
        if (error.code === error.TIMEOUT) {
          reject(new Error("Location request timed out"))
          return
        }
        reject(new Error("An unknown location error occurred"))
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    )
  })

export default function TeacherAttendance() {
  const [teacherAttendance, setTeacherAttendance] = useState([])
  const [isClockingIn, setIsClockingIn] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const [hasClockedOutToday, setHasClockedOutToday] = useState(false)
  const [timer, setTimer] = useState(0)
  const [startTime, setStartTime] = useState(null)
  const [locationError, setLocationError] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [isFaceModalOpen, setIsFaceModalOpen] = useState(false)
  const [isFacePreparing, setIsFacePreparing] = useState(false)
  const [isFaceProcessing, setIsFaceProcessing] = useState(false)
  const [faceError, setFaceError] = useState("")
  const [cameraReady, setCameraReady] = useState(false)
  const [capturedPreview, setCapturedPreview] = useState("")
  const [streamReady, setStreamReady] = useState(false)
  const [shouldForceMark, setShouldForceMark] = useState(false)
  const [pendingLocation, setPendingLocation] = useState(null)
  const [showOutsidePremisesConfirm, setShowOutsidePremisesConfirm] = useState(false)

  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)

  useEffect(() => {
    let intervalId

    if (isActive && startTime) {
      intervalId = setInterval(() => {
        setTimer(Math.floor((Date.now() - startTime) / 1000))
      }, 1000)
    }

    return () => clearInterval(intervalId)
  }, [isActive, startTime])

  useEffect(() => {
    const todayAttendance = teacherAttendance.find((record) => {
      const recordDate = new Date(record.date)
      const today = new Date()
      return recordDate.toDateString() === today.toDateString()
    })

    if (todayAttendance && todayAttendance.clockInTime && !todayAttendance.clockOutTime) {
      setIsActive(true)
      const clockInTs = new Date(todayAttendance.clockInTime).getTime()
      setStartTime(clockInTs)
      setTimer(Math.floor((Date.now() - clockInTs) / 1000))
      return
    }
    setIsActive(false)
    setStartTime(null)
    setTimer(0)
  }, [teacherAttendance])

  useEffect(() => {
    const todayAttendance = teacherAttendance.find((record) => {
      const recordDate = new Date(record.date)
      const today = new Date()
      return recordDate.toDateString() === today.toDateString()
    })

    setHasClockedOutToday(Boolean(todayAttendance && todayAttendance.clockOutTime))
  }, [teacherAttendance])

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const remainingSeconds = seconds % 60
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`
  }

  const fetchTeacherAttendance = useCallback(async () => {
    try {
      const user = getCurrentUser()
      const response = await axiosInstance.get(API_ENDPOINTS.teacherAttendance, {
        params: {
          teacherId: user.userId,
        },
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
      })
      const attendanceData = response.data.data?.attendance || []
      setTeacherAttendance(Array.isArray(attendanceData) ? attendanceData : [])
    } catch (error) {
      setTeacherAttendance([])
      setLocationError(error.response?.data?.message || error.message || "Failed to fetch teacher attendance")
    }
  }, [])

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    setStreamReady(false)
    setCameraReady(false)
  }, [streamRef])

  const closeFaceModal = useCallback(() => {
    setIsFaceModalOpen(false)
    setFaceError("")
    setCapturedPreview("")
    setIsFacePreparing(false)
    setIsFaceProcessing(false)
    setShouldForceMark(false)
    setPendingLocation(null)
    setShowOutsidePremisesConfirm(false)
    stopCamera()
  }, [stopCamera])

  const openFaceModal = useCallback(() => {
    setLocationError(null)
    setFaceError("")
    setCapturedPreview("")
    setShouldForceMark(false)
    setPendingLocation(null)
    setShowOutsidePremisesConfirm(false)
    setIsFaceModalOpen(true)
  }, [])

  const captureSelfie = useCallback(async () => {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas) {
      throw new Error("Camera is not ready")
    }

    const width = video.videoWidth
    const height = video.videoHeight
    if (!width || !height) {
      throw new Error("Camera frame is not available")
    }

    canvas.width = width
    canvas.height = height
    const context = canvas.getContext("2d")
    if (!context) {
      throw new Error("Could not capture selfie")
    }

    context.drawImage(video, 0, 0, width, height)
    const preview = canvas.toDataURL("image/jpeg", 0.85)
    setCapturedPreview(preview)
  }, [canvasRef, videoRef])

  const executeClockAction = useCallback(async (location, forceMark = false) => {
    const response = await axiosInstance.post(
      API_ENDPOINTS.teacherAttendanceClockIn,
      {
        latitude: location.latitude,
        longitude: location.longitude,
        attendanceMode: "webcam",
        ...(forceMark ? { forceMark: true } : {}),
      },
      {
        headers: getAuthHeaders(),
      },
    )

    const data = response.data || {}
    if (data.status === "location_warning") {
      setPendingLocation(location)
      setShouldForceMark(true)
      setShowOutsidePremisesConfirm(true)
      return { status: "location_warning" }
    }

    if (data.status !== "success") {
      throw new Error(data.message || "Failed to clock in/out")
    }

    if (data.data?.clockOutTime) {
      setIsActive(false)
      setTimer(0)
      setStartTime(null)
      setHasClockedOutToday(true)
    } else if (data.data?.clockInTime) {
      const clockInTs = new Date(data.data.clockInTime).getTime()
      setIsActive(true)
      setStartTime(clockInTs)
      setTimer(Math.floor((Date.now() - clockInTs) / 1000))
      setHasClockedOutToday(false)
    }

    await fetchTeacherAttendance()

    if (!data.data?.isWithinPremises) {
      alert("Attendance marked outside school premises.")
    } else {
      alert(data.message || "Attendance marked successfully")
    }

    closeFaceModal()
    return { status: "success" }
  }, [closeFaceModal, fetchTeacherAttendance])

  const runFaceClockFlow = useCallback(async () => {
    setIsClockingIn(true)
    setIsFaceProcessing(true)
    setFaceError("")
    setLocationError(null)
    try {
      await captureSelfie()
      const location = await getCurrentLocation()
      await executeClockAction(location, false)
    } catch (error) {
      const message = error.response?.data?.message || error.message || "Failed to clock in/out"
      setFaceError(message)
      setLocationError(message)
    } finally {
      setIsClockingIn(false)
      setIsFaceProcessing(false)
    }
  }, [captureSelfie, executeClockAction])

  const handleForceMarkConfirm = useCallback(async () => {
    if (!pendingLocation || !shouldForceMark) {
      setShowOutsidePremisesConfirm(false)
      return
    }
    setIsClockingIn(true)
    setIsFaceProcessing(true)
    setShowOutsidePremisesConfirm(false)
    try {
      await executeClockAction(pendingLocation, true)
    } catch (error) {
      const message = error.response?.data?.message || error.message || "Failed to clock in/out"
      setFaceError(message)
      setLocationError(message)
    } finally {
      setIsClockingIn(false)
      setIsFaceProcessing(false)
      setShouldForceMark(false)
      setPendingLocation(null)
    }
  }, [executeClockAction, pendingLocation, shouldForceMark])

  const handleClockButtonClick = useCallback(() => {
    if (isClockingIn || hasClockedOutToday) {
      return
    }
    openFaceModal()
  }, [hasClockedOutToday, isClockingIn, openFaceModal])

  useEffect(() => {
    if (!isFaceModalOpen) {
      stopCamera()
      return
    }

    let cancelled = false

    const prepare = async () => {
      try {
        setIsFacePreparing(true)
        setFaceError("")
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "user",
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        })

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }

        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          await videoRef.current.play()
          setStreamReady(true)
        }
      } catch (error) {
        setFaceError(error.message || "Unable to access camera")
      } finally {
        if (!cancelled) {
          setIsFacePreparing(false)
        }
      }
    }

    prepare()

    return () => {
      cancelled = true
      stopCamera()
    }
  }, [isFaceModalOpen, stopCamera, streamRef, videoRef])

  useEffect(() => {
    if (!streamReady || !videoRef.current) {
      return
    }

    const video = videoRef.current
    const onLoadedData = () => setCameraReady(true)
    video.addEventListener("loadeddata", onLoadedData)

    if (video.readyState >= 2) {
      setCameraReady(true)
    }

    return () => {
      video.removeEventListener("loadeddata", onLoadedData)
    }
  }, [streamReady, videoRef])

  const handlePageChange = (page) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  useEffect(() => {
    fetchTeacherAttendance()
  }, [fetchTeacherAttendance])

  const startIndex = (currentPage - 1) * itemsPerPage
  const currentAttendance = teacherAttendance.slice(startIndex, startIndex + itemsPerPage)

  const totalDays = teacherAttendance.length
  const presentDays = teacherAttendance.filter((r) => r.status === "present").length
  const absentDays = teacherAttendance.filter((r) => r.status === "absent").length
  const halfDayDays = teacherAttendance.filter((r) => r.status === "halfday" || r.status === "half-day").length
  const attendancePercentage = totalDays > 0 ? Math.round((presentDays / totalDays) * 100) : 0

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      <div className="bg-gray-100 p-4 border-b flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-800">My Attendance</h2>
        <div className="flex items-center gap-4">
          {isActive && <div className="text-lg font-semibold text-blue-600">{formatTime(timer)}</div>}
          <Button
            onClick={handleClockButtonClick}
            disabled={isClockingIn || hasClockedOutToday}
            className="flex items-center gap-2"
          >
            {isClockingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            {isActive ? "Clock Out" : "Clock In"}
          </Button>
        </div>
      </div>
      {locationError && <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 m-4">{locationError}</div>}

      {isFaceModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-5 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">{isActive ? "Selfie for Clock Out" : "Selfie for Clock In"}</h3>
              <Button variant="outline" onClick={closeFaceModal} disabled={isFaceProcessing || isClockingIn}>
                Close
              </Button>
            </div>

            {faceError && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {faceError}
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-xl border bg-gray-100 p-2">
                <div className="relative overflow-hidden rounded-lg bg-black">
                  <video ref={videoRef} className="h-72 w-full object-cover" autoPlay muted playsInline />
                  {!cameraReady && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-sm text-white">
                      {isFacePreparing ? "Preparing camera..." : "Starting camera..."}
                    </div>
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-600">Keep your face centered and ensure good lighting.</p>
              </div>

              <div className="rounded-xl border bg-gray-50 p-2">
                <div className="relative overflow-hidden rounded-lg bg-gray-200">
                  {capturedPreview ? (
                    <img src={capturedPreview} alt="Captured selfie" className="h-72 w-full object-cover" />
                  ) : (
                    <div className="flex h-72 items-center justify-center text-sm text-gray-600">Captured selfie preview</div>
                  )}
                </div>
                <p className="mt-2 text-xs text-gray-600">Your selfie is captured before geofencing and attendance marking.</p>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-end gap-3">
              <Button variant="outline" onClick={closeFaceModal} disabled={isFaceProcessing || isClockingIn}>
                Cancel
              </Button>
              <Button
                onClick={runFaceClockFlow}
                disabled={isFacePreparing || !cameraReady || isFaceProcessing || isClockingIn}
                className="min-w-40"
              >
                {isFaceProcessing || isClockingIn ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing
                  </>
                ) : (
                  "Capture & Continue"
                )}
              </Button>
            </div>

            <canvas ref={canvasRef} className="hidden" />
          </div>
        </div>
      )}

      {showOutsidePremisesConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl">
            <h4 className="text-lg font-semibold text-gray-900">Outside school premises</h4>
            <p className="mt-2 text-sm text-gray-600">
              You are not within school premises. Do you want to mark attendance anyway?
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowOutsidePremisesConfirm(false)
                  setPendingLocation(null)
                  setShouldForceMark(false)
                }}
                disabled={isFaceProcessing || isClockingIn}
              >
                Cancel
              </Button>
              <Button onClick={handleForceMarkConfirm} disabled={isFaceProcessing || isClockingIn}>
                {isFaceProcessing || isClockingIn ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Marking
                  </>
                ) : (
                  "Mark Anyway"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="p-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle>Attendance Records</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-left">Date</TableHead>
                        <TableHead className="text-left">Status</TableHead>
                        <TableHead className="text-left">Clock In</TableHead>
                        <TableHead className="text-left">Clock Out</TableHead>
                        <TableHead className="text-left">Duration</TableHead>
                        <TableHead className="text-left">Remarks</TableHead>
                        <TableHead className="text-left">Marked By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentAttendance.length > 0 ? (
                        currentAttendance.map((record) => (
                          <TableRow key={record._id}>
                            <TableCell className="text-left whitespace-nowrap">{new Date(record.date).toLocaleDateString()}</TableCell>
                            <TableCell className="text-left">
                              <div className="flex items-center justify-start">
                                <Badge
                                  variant={
                                    record.status === "present"
                                      ? "success"
                                      : record.status === "absent"
                                        ? "destructive"
                                        : record.status === "halfday" || record.status === "half-day"
                                          ? "warning"
                                          : "secondary"
                                  }
                                  className="w-fit"
                                >
                                  {record.status === "halfday" || record.status === "half-day" ? "Half Day" : record.status}
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell className="text-left whitespace-nowrap">
                              {record.clockInTime
                                ? new Date(record.clockInTime).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit",
                                  })
                                : "Not marked"}
                            </TableCell>
                            <TableCell className="text-left whitespace-nowrap">
                              {record.clockOutTime
                                ? new Date(record.clockOutTime).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit",
                                  })
                                : "Not marked"}
                            </TableCell>
                            <TableCell className="text-left whitespace-nowrap">
                              {record.clockInTime && record.clockOutTime
                                ? formatTime(
                                    Math.floor((new Date(record.clockOutTime) - new Date(record.clockInTime)) / 1000),
                                  )
                                : record.clockInTime && !record.clockOutTime && isActive
                                  ? formatTime(Math.floor((Date.now() - new Date(record.clockInTime)) / 1000))
                                  : "Not available"}
                            </TableCell>
                            <TableCell className="text-left">{record.remarks || "No remarks"}</TableCell>
                            <TableCell className="text-left">{record.markedBy}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-4">
                            No attendance records found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                <Pagination
                  currentPage={currentPage}
                  totalItems={teacherAttendance.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={setItemsPerPage}
                  itemLabel="records"
                />
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-500" />
                  Statistics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total Days</span>
                    <span className="font-semibold">{totalDays}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 flex items-center gap-1">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Present
                    </span>
                    <span className="font-semibold text-green-600">{presentDays}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 flex items-center gap-1">
                      <XCircle className="h-4 w-4 text-red-500" />
                      Absent
                    </span>
                    <span className="font-semibold text-red-600">{absentDays}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 flex items-center gap-1">
                      <Clock className="h-4 w-4 text-orange-500" />
                      Half Day
                    </span>
                    <span className="font-semibold text-orange-600">{halfDayDays}</span>
                  </div>
                  <div className="pt-2 border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Attendance Rate</span>
                      <span className="font-bold text-blue-600">{attendancePercentage}%</span>
                    </div>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${attendancePercentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CalendarDays className="h-5 w-5 text-blue-500" />
                  Calendar
                </CardTitle>
              </CardHeader>
              <CardContent>
                <StudentCalendarModal attendance={teacherAttendance} showDirectly={false} />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}

